import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '../stores/auth.js';

const routes = [
  { path: '/login', name: 'login', component: () => import('../views/LoginView.vue'), meta: { public: true } },
  { path: '/register/:token', name: 'register', component: () => import('../views/InviteView.vue'), meta: { public: true } },
  { path: '/reset-password/:token', name: 'reset-password', component: () => import('../views/InviteView.vue'), meta: { public: true } },
  { path: '/legal/:document?', name: 'legal', component: () => import('../views/LegalView.vue'), meta: { public: true } },
  {
    path: '/feedback/:restaurantId',
    name: 'feedback',
    component: () => import('../views/FeedbackView.vue'),
    meta: { public: true, title: 'Обратная связь' }
  },
  {
    path: '/',
    component: () => import('../layouts/AppShell.vue'),
    children: [
      { path: '', name: 'dashboard', component: () => import('../views/DashboardView.vue') },
      { path: 'chat/:roomId?', name: 'chat', component: () => import('../views/ChatView.vue') },
      { path: 'employees', name: 'employees', component: () => import('../views/EmployeesView.vue') },
      {
        path: 'employees/:employeeId',
        name: 'employee-detail',
        meta: { title: 'Карточка сотрудника' },
        component: () => import('../views/EmployeeProfileView.vue')
      },
      {
        path: 'employees/:employeeId/settings',
        name: 'employee-settings',
        meta: { title: 'Настройка сотрудника' },
        redirect: (to) => ({
          name: 'employee-detail',
          params: { employeeId: to.params.employeeId },
          query: { ...to.query, edit: '1' }
        })
      },
      { path: 'schedule', name: 'schedule', meta: { permissions: ['schedule.self', 'schedule.manage'], title: 'График' }, component: () => import('../views/ScheduleView.vue') },
      { path: 'payroll', name: 'payroll', meta: { permission: 'payroll.tab', title: 'ФОТ' }, component: () => import('../views/PayrollView.vue') },
      {
        path: 'banking',
        name: 'banking',
        meta: { permission: 'billing.tab', title: 'Банковские счета' },
        component: () => import('../views/BankingView.vue')
      },
      {
        path: 'invoices',
        name: 'supplier-invoices',
        meta: { permission: 'budget.tab', title: 'Счета поставщиков' },
        component: () => import('../views/InvoicesView.vue')
      },
      {
        path: 'budget',
        name: 'budget',
        meta: { permission: 'budget.tab', title: 'Бюджет' },
        component: () => import('../views/BudgetView.vue')
      },
      { path: 'settings', name: 'settings', component: () => import('../views/SettingsView.vue') },
      {
        path: 'tasks',
        name: 'tasks',
        component: () => import('../views/TasksView.vue')
      },
      {
        path: 'tasks/:taskId',
        name: 'task-detail',
        component: () => import('../views/TasksView.vue')
      },
      {
        path: 'calendar',
        name: 'calendar',
        component: () => import('../views/TaskCalendarView.vue')
      },
      {
        path: 'journals',
        name: 'journals',
        meta: { permission: 'journals.fill', title: 'Журналы' },
        component: () => import('../views/JournalsView.vue')
      },
      {
        path: 'audit',
        name: 'audit',
        meta: { permission: 'audit.tab', title: 'Санитарный аудит' },
        component: () => import('../views/AuditView.vue')
      },
      {
        path: 'audit/:auditId',
        name: 'audit-detail',
        meta: { permission: 'audit.tab', title: 'Отчёт аудита' },
        component: () => import('../views/AuditView.vue')
      },
      {
        path: 'inventory',
        redirect: '/inventory/receipts'
      },
      {
        path: 'inventory/receipts/:receiptId?',
        name: 'inventory-receipts',
        meta: { permission: 'inventory.tab', title: 'Склад', inventorySection: 'receipts' },
        component: () => import('../views/InventoryView.vue')
      },
      {
        path: 'inventory/balances',
        name: 'inventory-balances',
        meta: { permission: 'inventory.tab', title: 'Склад', inventorySection: 'balances' },
        component: () => import('../views/InventoryView.vue')
      },
      {
        path: 'inventory/stocktakes',
        name: 'inventory-stocktakes',
        meta: { permission: 'inventory.tab', title: 'Склад', inventorySection: 'stocktakes' },
        component: () => import('../views/InventoryView.vue')
      },
      {
        path: 'inventory/reconciliation',
        name: 'inventory-reconciliation',
        meta: { permission: 'inventory.tab', title: 'Склад', inventorySection: 'reconciliation' },
        component: () => import('../views/InventoryView.vue')
      },
      {
        path: 'inventory/recipes',
        name: 'inventory-recipes',
        meta: { permission: 'inventory.tab', title: 'Склад', inventorySection: 'recipes' },
        component: () => import('../views/InventoryView.vue')
      },
      {
        path: 'inventory/prices',
        name: 'inventory-prices',
        meta: { permission: 'inventory.tab', title: 'Склад', inventorySection: 'prices' },
        component: () => import('../views/InventoryView.vue')
      },
      {
        path: 'inventory/settings',
        name: 'inventory-settings',
        meta: { permission: 'integrations.manage', title: 'Склад', inventorySection: 'settings' },
        component: () => import('../views/InventoryView.vue')
      },
      {
        path: 'vision',
        name: 'vision',
        meta: { permissions: ['vision.self', 'vision.tab'], title: 'Vision' },
        component: () => import('../views/VisionView.vue')
      },
      {
        path: 'analytics',
        redirect: '/analytics/overview'
      },
      {
        path: 'analytics/overview',
        name: 'analytics-overview',
        meta: { permission: 'metrics.tab', title: 'Показатели', analyticsSection: 'overview', viewKey: 'analytics' },
        component: () => import('../views/AnalyticsView.vue')
      },
      {
        path: 'analytics/revenue',
        name: 'analytics-revenue',
        meta: { permission: 'metrics.tab', title: 'Показатели', analyticsSection: 'revenue', viewKey: 'analytics' },
        component: () => import('../views/AnalyticsView.vue')
      },
      {
        path: 'analytics/checks',
        name: 'analytics-checks',
        meta: { permission: 'metrics.tab', title: 'Показатели', analyticsSection: 'checks', viewKey: 'analytics' },
        component: () => import('../views/AnalyticsView.vue')
      },
      {
        path: 'analytics/dishes',
        name: 'analytics-dishes',
        meta: { permission: 'metrics.tab', title: 'Показатели', analyticsSection: 'dishes', viewKey: 'analytics' },
        component: () => import('../views/AnalyticsView.vue')
      },
      {
        path: 'analytics/employees',
        name: 'analytics-employees',
        meta: { permission: 'metrics.tab', title: 'KPI сотрудников', analyticsSection: 'employees', viewKey: 'analytics' },
        component: () => import('../views/EmployeeKpiView.vue')
      },
      {
        path: 'analytics/restaurants',
        name: 'analytics-restaurants',
        redirect: '/analytics/overview',
        meta: { permission: 'metrics.tab', title: 'Показатели', viewKey: 'analytics' }
      },
      {
        path: 'analytics/temperature/settings',
        name: 'analytics-temperature-settings',
        redirect: { path: '/analytics/settings', query: { focus: 'temperature' } },
        meta: { permission: 'metrics.configure', title: 'Настройки показателей', analyticsSection: 'settings', viewKey: 'analytics' }
      },
      {
        path: 'analytics/temperature',
        name: 'analytics-temperature',
        meta: { permission: 'metrics.tab', title: 'Температура оборудования', analyticsSection: 'temperature', viewKey: 'analytics' },
        component: () => import('../views/TemperatureView.vue')
      },
      {
        path: 'analytics/settings',
        name: 'analytics-settings',
        meta: { permission: 'metrics.configure', title: 'Настройки показателей', analyticsSection: 'settings', viewKey: 'analytics' },
        component: () => import('../views/AnalyticsView.vue')
      },
      {
        path: 'reviews',
        name: 'reviews',
        meta: { permission: 'metrics.tab', title: 'Отзывы', reviewsSection: 'overview', viewKey: 'reviews' },
        component: () => import('../views/ReviewsView.vue')
      },
      {
        path: 'reviews/problems',
        name: 'reviews-problems',
        redirect: '/reviews',
        meta: { permission: 'metrics.tab', title: 'Отзывы', reviewsSection: 'overview', viewKey: 'reviews' }
      },
      {
        path: 'reviews/settings',
        name: 'reviews-settings',
        meta: { permission: 'metrics.configure', title: 'Настройки отзывов', reviewsSection: 'settings', viewKey: 'reviews' },
        component: () => import('../views/ReviewsView.vue')
      }
    ]
  },
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('../views/NotFoundView.vue'), meta: { public: true } }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.name === 'employees' && from.name === 'employee-detail') {
      return { top: Number(sessionStorage.getItem('employees:listScroll') || 0) };
    }
    return { top: 0 };
  }
});

router.beforeEach(async (to) => {
  const auth = useAuthStore();
  await auth.bootstrap();
  if (!to.meta.public && !auth.authenticated) return { name: 'login', query: { next: to.fullPath } };
  if (to.name === 'login' && auth.authenticated) return { name: 'dashboard' };
  if (!to.meta.public && to.meta.permission && !auth.hasPermission(to.meta.permission)) return { name: 'dashboard' };
  if (!to.meta.public && to.meta.permissions && !to.meta.permissions.some((permission) => auth.hasPermission(permission))) return { name: 'dashboard' };
  return true;
});

export default router;
